import React, { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { nanoid } from 'nanoid';
import copy from 'copy-to-clipboard';

import { ShareLinkFieldType, ShareLinkFormType } from '../../form/link-config-form.factory';
import { Button } from 'src/common/button';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { apiCreateShortLink, apiGetShortLinks, apiGetJourneyAlias, apiUpdateJourneyAlias, apiDeleteShortLink } from 'src/utils/journeyApi';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

interface EmailEntry {
  id: string;
  name: string;
  email: string;
  shortLink: string;
}

export const ShareConfigEmailParamsPanel = () => {
  const { values, setFieldValue } = useFormikContext<ShareLinkFormType>();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailEntries, setEmailEntries] = useState<EmailEntry[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [shortLinkToDelete, setShortLinkToDelete] = useState<EmailEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { subscriptionPlanType, isOnTrial, features, getUpgradeMessage } = useFeatureRestrictions();
  // Load email_param_enabled value from backend
  useEffect(() => {
    const fetchAliasSettings = async () => {
      try {
        if (values.uuid) {
          const response = await apiGetJourneyAlias(values.uuid);
          console.log("first ",values, " response ", response)
          if (response && response.email_param_enabled !== undefined) {
            setFieldValue('email_param_enabled', response.email_param_enabled);
          }
          if (response && response.org_slug) {
            setOrganizationSlug(response.org_slug);
          }
        }
      } catch (error) {
        console.error('Error loading alias settings:', error);
      }
    };

    fetchAliasSettings();
  }, [values.uuid, setFieldValue]);

  // Load existing shortlinks when component mounts
  useEffect(() => {
    const fetchShortLinks = async () => {
      try {
        setIsLoading(true);
        const response = await apiGetShortLinks(values.uuid);
        if (response && response.shortlinks) {
          // Transform API response into EmailEntry format
          const entries = response.shortlinks.map((link: any) => {
            // Use organizationSlug fetched from the backend
            const orgSlug = organizationSlug;
            
            // Construct the shortlink
            const shortLink = `${process.env.HTTP_PROTOCOL}://${orgSlug}.${process.env.ROOT_NETLOC?.split(':')[0]}/p/${values.slug}?sl=${link.short_id}`;
            
            return {
              id: link.short_id,
              name: link.name || '',
              email: link.email,
              shortLink
            };
          });
          setEmailEntries(entries);
        }
      } catch (error) {
        console.error('Error loading short links:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (values.uuid) {
      fetchShortLinks();
    }
  }, [values.uuid, values.slug, organizationSlug]);

  const onFieldChange = (payload: ShareLinkFieldType<unknown>) => setFieldValue(payload.field, payload.value);

  const toggleEmailParams = () => {
    const newValue = !values.email_param_enabled;
    onFieldChange({ field: 'email_param_enabled', value: newValue });
    
    // Save the changed value to the database
    apiUpdateJourneyAlias(values.uuid, { email_param_enabled: newValue })
      .then(() => {
        setSuccessNotification(newValue ? 'Email parameter enabled' : 'Email parameter disabled');
      })
      .catch((error) => {
        // Revert the UI change if the API call fails
        onFieldChange({ field: 'email_param_enabled', value: !newValue });
        setErrorNotification('Failed to update email parameter setting');
        console.error('Error updating email parameter setting:', error);
      });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const generateShortLink = async () => {
    if (!name.trim() || !email.trim()) {
      setErrorNotification('Please enter both name and email');
      return;
    }

    if (!email.includes('@')) {
      setErrorNotification('Please enter a valid email address');
      return;
    }

    if (!organizationSlug) {
      setErrorNotification('Organization slug not available');
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    try {
      // Create a unique ID for the short link
      const shortId = nanoid(8);
      
      // Make API call to create a short link entry
      const response = await apiCreateShortLink(values.uuid, shortId, {
        name: name.trim(),
        email: email.trim()
      });
      
      // Use organizationSlug fetched from backend

      // Construct the shortlink
      const constructedShortLink = `${process.env.HTTP_PROTOCOL}://${organizationSlug}.${process.env.ROOT_NETLOC?.split(':')[0]}/p/${values.slug}?sl=${shortId}`;
      
      // Add the new entry to the list using the constructedShortLink
      setEmailEntries([
        ...emailEntries,
        {
          id: shortId,
          name: name.trim(),
          email: email.trim(),
          shortLink: constructedShortLink
        }
      ]);
      
      // Clear the form
      setName('');
      setEmail('');
      setSuccessNotification('Short link generated successfully');
    } catch (error) {
      setErrorNotification('Failed to generate short link');
      console.error('Error generating short link:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (shortLink: string) => {
    copy(shortLink);
    setSuccessNotification('Link copied to clipboard');
  };
  // Functions for deleting shortlinks
  const openDeleteConfirm = (entry: EmailEntry) => {
    setShortLinkToDelete(entry);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setShortLinkToDelete(null);
  };

  const handleDeleteShortLink = async () => {
    if (!shortLinkToDelete) return;
    
    setIsDeleting(true);
    try {
      await apiDeleteShortLink(values.uuid, shortLinkToDelete.id);
      
      // Remove the entry from the local state
      setEmailEntries(emailEntries.filter(entry => entry.id !== shortLinkToDelete.id));
      setSuccessNotification('Short link deleted successfully');
    } catch (error) {
      setErrorNotification('Failed to delete short link');
      console.error('Error deleting short link:', error);
    } finally {
      setIsDeleting(false);
      closeDeleteConfirm();
    }
  };

  // Restriction: Show ONLY paywall if on Start/Grow (starter/pro) and not on trial
  if ((subscriptionPlanType === 'starter' || subscriptionPlanType === 'pro') && !isOnTrial && !features.canAccessEmailTracking) {
    return (
      <div className='flex flex-col divide-y'>
        <div className='py-6'>
          <div className='text-bedrock-p-strong mb-2'>Email Get Parameter</div>
          <div className='text-bedrock-p text-bedrock-gray-dark mb-4'>
            When enabled, email will be pre-filled from URL parameter if present. For example: ?email=test@example.com
          </div>
          
          <div className='flex items-center mb-6 opacity-50 pointer-events-none'>
            <div 
              className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200 ${
                values.email_param_enabled 
                  ? 'bg-bedrock-blue' 
                  : 'bg-bedrock-gray-medium'
              }`}
              role="switch"
              aria-checked={values.email_param_enabled}
            >
              <span 
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-black shadow transform transition ease-in-out duration-200 ${
                  values.email_param_enabled 
                    ? 'translate-x-5' 
                    : 'translate-x-0 bg-white'
                }`}
              />
            </div>
            <span className='ml-3 text-bedrock-p text-bedrock-gray-dark'>
              {values.email_param_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <div className='mt-6 border-t pt-6'>
            <div className='text-bedrock-p-strong mb-2'>Generate Personalized Short Links</div>
            <div className='text-bedrock-p text-bedrock-gray-dark mb-4'>
              Create personalized links that will automatically register visitors with their name and email.
            </div>
            
            <div className='flex flex-col space-y-4 mb-6 opacity-50 pointer-events-none'>
              <div>
                <label htmlFor='visitor-name' className='block text-bedrock-p-small mb-1'>
                  Visitor Name
                </label>
                <input
                  type='text'
                  id='visitor-name'
                  value={name}
                  onChange={handleNameChange}
                  placeholder='Enter visitor name'
                  className='w-full p-2 border rounded-md'
                  disabled
                />
              </div>
              
              <div>
                <label htmlFor='visitor-email' className='block text-bedrock-p-small mb-1'>
                  Visitor Email
                </label>
                <input
                  type='email'
                  id='visitor-email'
                  value={email}
                  onChange={handleEmailChange}
                  placeholder='Enter visitor email'
                  className='w-full p-2 border rounded-md'
                  disabled
                />
              </div>
              
              <Button
                label={isGenerating ? 'Generating...' : 'Generate Short Link'}
                variant='primary'
                size='regular'
                onClick={generateShortLink}
                disabled
                className='self-start'
              />
            </div>
          </div>
        </div>

        {/* Upgrade message and button */}
        <div className='py-6 flex flex-col items-start gap-4'>
          <div className='text-bedrock-p-strong mb-2'>Upgrade Required</div>
          <div className='text-bedrock-p text-bedrock-gray-dark mb-2'>
            {getUpgradeMessage('email_tracking') || 'Upgrade to Grow or Scale plan to use Email GET parameter.'}
          </div>
          <Button
            label='Upgrade'
            variant='primary'
            size='regular'
            onClick={() => window.location.assign('/billing')}
            className='mt-2'
          />
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col divide-y'>
      <div className='py-6'>
        <div className='text-bedrock-p-strong mb-2'>Email Get Parameter</div>
        <div className='text-bedrock-p text-bedrock-gray-dark mb-4'>
          When enabled, email will be pre-filled from URL parameter if present. For example: ?email=test@example.com
        </div>
        
        <div className='flex items-center mb-6'>
          <div 
            className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 ${
              values.email_param_enabled 
                ? 'bg-bedrock-blue' 
                : 'bg-bedrock-gray-medium'
            }`}
            onClick={toggleEmailParams}
            role="switch"
            aria-checked={values.email_param_enabled}
          >
            <span 
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-black shadow transform transition ease-in-out duration-200 ${
                values.email_param_enabled 
                  ? 'translate-x-5' 
                  : 'translate-x-0 bg-white'
              }`}
            />
          </div>
          <span className='ml-3 text-bedrock-p text-bedrock-gray-dark'>
            {values.email_param_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Restriction logic for Start/Grow plans (not on trial) */}
        {values.email_param_enabled &&
          ((subscriptionPlanType === 'starter' || subscriptionPlanType === 'pro') && !isOnTrial && !features.canAccessEmailTracking) ? (
          <div className='mt-6 border-t pt-6 flex flex-col items-start gap-4'>
            <div className='text-bedrock-p-strong mb-2'>Upgrade Required</div>
            <div className='text-bedrock-p text-bedrock-gray-dark mb-2'>
              {getUpgradeMessage('email_tracking') || 'Upgrade to Grow or Scale plan to use Email GET parameter.'}
            </div>
            <div className='flex items-center gap-2'>
              <input
                type='text'
                value={`?email=test@example.com`}
                readOnly
                className='w-64 p-2 border rounded-md mr-2 bg-gray-50 text-gray-700'
                onFocus={e => e.target.select()}
              />
              <Button
                label='Copy'
                variant='secondary'
                size='small'
                onClick={() => copyToClipboard('?email=test@example.com')}
              />
            </div>
            <Button
              label='Upgrade'
              variant='primary'
              size='regular'
              onClick={() => window.location.assign('/billing')}
              className='mt-2'
            />
          </div>
        ) : (
          <div className='mt-6 border-t pt-6'>
            <div className='text-bedrock-p-strong mb-2'>Generate Personalized Short Links</div>
            <div className='text-bedrock-p text-bedrock-gray-dark mb-4'>
              Create personalized links that will automatically register visitors with their name and email.
            </div>
            
            <div className='flex flex-col space-y-4 mb-6'>
              <div>
                <label htmlFor='visitor-name' className='block text-bedrock-p-small mb-1'>
                  Visitor Name
                </label>
                <input
                  type='text'
                  id='visitor-name'
                  value={name}
                  onChange={handleNameChange}
                  placeholder='Enter visitor name'
                  className='w-full p-2 border rounded-md'
                />
              </div>
              
              <div>
                <label htmlFor='visitor-email' className='block text-bedrock-p-small mb-1'>
                  Visitor Email
                </label>
                <input
                  type='email'
                  id='visitor-email'
                  value={email}
                  onChange={handleEmailChange}
                  placeholder='Enter visitor email'
                  className='w-full p-2 border rounded-md'
                />
              </div>
              
              <Button
                label={isGenerating ? 'Generating...' : 'Generate Short Link'}
                variant='primary'
                size='regular'
                onClick={generateShortLink}
                disabled={isGenerating || !values.email_param_enabled}
                className='self-start'
              />
            </div>
            
            {isLoading ? (
              <div className='text-bedrock-p text-bedrock-gray-dark'>Loading links...</div>
            ) : emailEntries.length > 0 && (
              <div className='mt-4'>
                <div className='text-bedrock-p-strong mb-2'>Generated Links</div>
                <div className='max-h-60 overflow-y-auto'>
                  {emailEntries.map((entry) => (
                    <div key={entry.id} className='border rounded-md p-3 mb-2'>
                      <div className='flex justify-between items-center'>
                        <div>
                          <div className='text-bedrock-p-small font-semibold'>{entry.name}</div>
                          <div className='text-bedrock-p-small text-bedrock-gray-dark'>{entry.email}</div>
                          <div className='text-bedrock-p-small text-bedrock-blue-dark mt-1 break-all'>
                            {entry.shortLink}
                          </div>
                        </div>

                        <div className='flex space-x-2'>
                          <Button
                            label='Copy'
                            variant='secondary'
                            size='small'
                            onClick={() => copyToClipboard(entry.shortLink)}
                          />
                          <Button
                            label='Delete'
                            variant='tertiary'
                            size='small'
                            onClick={() => openDeleteConfirm(entry)}
                            className='text-red-600 hover:text-red-800'
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && shortLinkToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-bedrock-h3 mb-4">Delete Short Link</h3>
            <p className="text-bedrock-p mb-2">
              Are you sure you want to delete the short link for:
            </p>
            <div className="mb-4 p-3 bg-gray-100 rounded">
              <div className="font-semibold">{shortLinkToDelete.name}</div>
              <div className="text-bedrock-gray-dark">{shortLinkToDelete.email}</div>
            </div>
            <div className="flex justify-end space-x-4">
              <Button 
                label="Cancel" 
                variant="secondary" 
                size="regular" 
                onClick={closeDeleteConfirm}
                disabled={isDeleting}
              />
              <Button 
                label={isDeleting ? "Deleting..." : "Delete"} 
                variant="primary" 
                size="regular" 
                onClick={handleDeleteShortLink}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 